import type { ToolResult } from "@/types/tools";

export function formatFileSize(bytes: number) {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return "0 B";
  }

  const units = ["B", "KB", "MB", "GB"] as const;
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exponent;

  return `${value.toFixed(value >= 10 || exponent === 0 ? 0 : 1)} ${units[exponent]}`;
}

export function parsePageRanges(input: string, pageCount: number): ToolResult<number[]> {
  if (!Number.isInteger(pageCount) || pageCount < 1) {
    return {
      ok: false,
      error: "PDF page count could not be read.",
    };
  }

  const trimmed = input.trim();

  if (!trimmed) {
    return {
      ok: false,
      error: "Enter one or more page ranges.",
    };
  }

  const pages: number[] = [];
  const seen = new Set<number>();
  const tokens = trimmed.split(/[,\s]+/).filter(Boolean);

  for (const token of tokens) {
    const match = /^(\d+)(?:-(\d+))?$/.exec(token);

    if (!match) {
      return {
        ok: false,
        error: `Page range "${token}" is not valid.`,
      };
    }

    const start = Number(match[1]);
    const end = match[2] ? Number(match[2]) : start;

    if (start < 1 || end < 1 || start > pageCount || end > pageCount) {
      return {
        ok: false,
        error: `Page range "${token}" is outside 1-${pageCount}.`,
      };
    }

    if (start > end) {
      return {
        ok: false,
        error: `Page range "${token}" must start before it ends.`,
      };
    }

    for (let page = start; page <= end; page += 1) {
      const pageIndex = page - 1;

      if (!seen.has(pageIndex)) {
        seen.add(pageIndex);
        pages.push(pageIndex);
      }
    }
  }

  return {
    ok: true,
    value: pages,
  };
}
